import { Fragment, useState, useEffect } from "react";
import {
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonIcon,
  IonContent,
  IonGrid,
  IonRow,
  IonCol,
  IonFooter,
  IonText,
  IonItem,
  IonLabel,
} from "@ionic/react";
import { close, addOutline } from "ionicons/icons";
import { useSelectMemberPopover } from "../hooks/useSelectMemberPopover";
import { useSelectRolePopover } from "../hooks/useSelectRolePopover";
import { useSelectTypePopover } from "../hooks/useSelectTypePopover";
import { useGroupReports } from "../hooks/useGroupReports";
import ContactChips, { ContactChipItem } from "./ContactChips";
import { displayMsg, Spinner } from "pymble-ui/components";
import { CSVLink } from "react-csv";

import "./MemberSearch.css";
import { GroupRoleChips, GroupTypeChips } from "pymble-ui/components";

interface ContactItem {
  _id: string;
  Name?: string;
  ContactIdLookup?: string;
  [key: string]: unknown;
}

interface ReportRow {
  _id: { MemberName: string };
  MemberGroups: string[];
}

interface MemberSearchProps {
  onDismiss: () => void;
  selectedGroups: (string | number)[];
}

const MemberSearch: React.FC<MemberSearchProps> = ({
  onDismiss,
  selectedGroups: _selectedGroups,
}) => {
  const [selectedContacts, setSelectedContacts] = useState<ContactItem[]>([]);
  const [allowAction, setAllowAction] = useState<boolean>(false);
  const [showSpinner, setShowSpinner] = useState<boolean>(false);

  const { presentContactPopover, selectedMember, MemberModal } = useSelectMemberPopover(
    selectedContacts,
    "Find Contacts to Include"
  );
  const {
    presentRoles,
    clearRoleFilter,
    removeRole,
    selectedRoles,
    RolesPopover,
  } = useSelectRolePopover();

  const {
    presentTypes,
    clearTypeFilter,
    removeType,
    selectedTypes,
    TypesPopover,
  } = useSelectTypePopover();

  const { reportData, refreshGroupMemberData } =
    useGroupReports();
  const [reportCsvData, setReportCsvData] = useState<string[][]>([]);

  useEffect(() => {
    const csvData: string[][] = [];
    (reportData as ReportRow[]).forEach((member) => {
      let gCount = 0;
      member.MemberGroups.forEach((g) => {
        if (gCount === 0) {
          csvData[csvData.length] = [member._id.MemberName, g];
        } else {
          csvData[csvData.length] = ["", g];
        }
        gCount++;
      });
    });
    setReportCsvData(csvData);
  }, [reportData]);

  useEffect(() => {
    if (selectedMember) {
      setSelectedContacts([...selectedContacts, selectedMember as ContactItem]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- selectedContacts causes infinite loop
  }, [selectedMember]);

  const handleClearSelectedContacts = () => {
    setSelectedContacts([]);
  };
  const handleRemoveContact = (contact: ContactChipItem) => {
    const newSelected = selectedContacts.filter(
      (c) => c.ContactIdLookup !== contact.ContactIdLookup
    );
    setSelectedContacts(newSelected);
  };

  const handleQuery = async () => {
    setShowSpinner(true);
    const contacts = selectedContacts.map((c) => c.ContactIdLookup ?? c._id);
    const roles = selectedRoles.map((r) => r.Name);
    const types = selectedTypes.map((t) => t.Name ?? "");

    displayMsg("Fetching results....", { type: "success", position: "middle" });

    await refreshGroupMemberData(roles, contacts, types);

    setShowSpinner(false);
  };

  // Manage our allow search state.
  // Must have either roles or contacts selected before a search is
  // allowed.
  useEffect(() => {
    if (
      (Array.isArray(selectedContacts) && selectedContacts.length) ||
      (Array.isArray(selectedTypes) && selectedTypes.length) ||
      (Array.isArray(selectedRoles) && selectedRoles.length)
    ) {
      setAllowAction(true);
    } else {
      setAllowAction(false);
    }
  }, [selectedRoles, selectedContacts, selectedTypes]);

  return (
    <Fragment>
      <IonHeader>
        <IonToolbar color="light">
          <IonTitle>Member Search</IonTitle>
          <IonButtons slot="end">
            <IonButton fill="clear" size="small" onClick={onDismiss}>
              <IonIcon slot="icon-only" icon={close} size="small" />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <IonGrid>
          <IonRow>
            <IonCol>
              <IonItem>
                <IonLabel>Contacts Filter</IonLabel>
                <IonButton
                  className="ion-text-capitalize"
                  color="secondary"
                  fill="solid"
                  onClick={() =>
                    presentContactPopover({
                      cssClass: "group-member-modal",
                    })
                  }
                >
                  <IonIcon slot="icon-only" icon={addOutline} />
                </IonButton>
              </IonItem>
            </IonCol>
            <IonCol>
              <IonItem>
                <IonLabel>Group Types Filter</IonLabel>
                <IonButton
                  className="ion-text-capitalize"
                  color="secondary"
                  fill="solid"
                  size="small"
                  onClick={(e) =>
                    presentTypes({
                      event: e.nativeEvent,
                    })
                  }
                >
                  <IonIcon slot="icon-only" icon={addOutline} />
                </IonButton>
              </IonItem>
            </IonCol>
            <IonCol>
              <IonItem>
                <IonLabel>Member Roles Filter</IonLabel>
                <IonButton
                  className="ion-text-capitalize"
                  color="secondary"
                  fill="solid"
                  size="small"
                  onClick={(e) =>
                    presentRoles({
                      event: e.nativeEvent,
                    })
                  }
                >
                  <IonIcon slot="icon-only" icon={addOutline} />
                </IonButton>
              </IonItem>
            </IonCol>
          </IonRow>
          <IonRow>
            <IonCol>
              <ContactChips
                contacts={selectedContacts}
                onClearContacts={handleClearSelectedContacts}
                onRemoveContact={handleRemoveContact}
              />
            </IonCol>
            <IonCol>
              <GroupTypeChips
                groupTypes={selectedTypes}
                onRemove={removeType}
                onClear={() => clearTypeFilter(true)}
              />
            </IonCol>
            <IonCol>
              <GroupRoleChips
                roles={selectedRoles}
                onRemove={removeRole}
                onClear={() => clearRoleFilter(true)}
              />
            </IonCol>
          </IonRow>
        </IonGrid>

        <IonGrid>
          {reportData &&
            (reportData as ReportRow[]).map((d, i: number) => {
              return (
                <IonRow key={i}>
                  <IonCol>{d._id.MemberName}</IonCol>
                  <IonCol>
                    {d.MemberGroups &&
                      d.MemberGroups.map((g, j: number) => {
                        return (
                          <Fragment key={j}>
                            <IonText>{g}</IonText>
                            <br />
                          </Fragment>
                        );
                      })}
                  </IonCol>
                </IonRow>
              );
            })}
        </IonGrid>
        {MemberModal}
        {RolesPopover}
        {TypesPopover}
      </IonContent>
      <IonFooter>
        <IonToolbar>
          <IonItem lines="none" slot="start">
            <IonLabel>
              {reportData && `Members found: ${(reportData as ReportRow[]).length}`}
            </IonLabel>
          </IonItem>
          <IonButtons slot="end">
            {showSpinner && <Spinner />}
            {reportCsvData && reportCsvData.length ? (
              <IonButton
                className="ion-text-capitalize"
                color="secondary"
                fill="solid"
                disabled={!allowAction || showSpinner}
              >
                <CSVLink
                  data={reportCsvData}
                  filename={"portal-group-members.csv"}
                  className="group-report-btn"
                >
                  Download CSV
                </CSVLink>
              </IonButton>
            ) : (
              ""
            )}
            <IonButton
              className="ion-text-capitalize"
              color="secondary"
              fill="solid"
              onClick={handleQuery}
              disabled={!allowAction || showSpinner}
            >
              Search
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonFooter>
    </Fragment>
  );
};

export default MemberSearch;
